import {useQuery} from '@tanstack/react-query';
import React from 'react';

import {MultiFeatureAnalysisView} from 'app/components/Dashboard/MultiFeatureAnalysisWidget/MultiFeatureAnalysisWidget';
import {MultiFeatureCategoricalAnalysisView} from 'app/components/Dashboard/MultiFeatureAnalysisWidget/MultiFeatureCategoricalAnalysisWidget';
import LensLogo from 'app/components/Logo/LensLogo';
import {api} from 'app/modules/Remote';
import {DASHBOARD_ERRORS, UNIT_AREA_ACRE} from 'app/utils/constants';
import {getLayerKeyFromSourceAndLayer} from 'app/utils/layerUtils';

import {
  CategoricalStatResponse,
  FeatureStatsApiResponse,
  MFAResponse,
  MultifeatureAnalysisConfig,
} from '../types';
import cs from './PublicWidget.module.scss';

const PublicWidget = ({uuid}: {uuid: string}) => {
  const {
    data: widgetData,
    isLoading: isWidgetLoading,
    error: widgetError,
  } = useQuery({
    queryKey: ['publicWidgetConfig', uuid],
    queryFn: async () => {
      // Fetch paginated data and return the merged stats map
      const response = await api.publicWidget(uuid);
      const {data, config} = response.toJS() as {
        data: FeatureStatsApiResponse;
        config: MultifeatureAnalysisConfig;
      };

      config.layerKey = getLayerKeyFromSourceAndLayer(config.sourceId, config.layerKey);

      return {data, config};
    },
  });

  const isCategorical =
    widgetData?.config?.statAggregation === 'area_counts' ||
    widgetData?.config?.statAggregation === 'area_percentages';

  const noDataFound =
    !isWidgetLoading &&
    widgetData?.data &&
    Object.values(widgetData.data).every(
      (feature) => feature.error === DASHBOARD_ERRORS.NO_STATISTICS_FOUND
    );

  return (
    <div className={cs.publicWidgetPage}>
      <header className={cs.publicWidgetHeader}>
        <LensLogo
          className={cs.publicWidgetLogo}
          onClick={() => window.open('https://www.upstream.tech/lens', '_blank')}
        />
      </header>
      <div className={cs.publicWidgetContainer}>
        {widgetData?.data &&
          widgetData.config &&
          (isCategorical ? (
            <MultiFeatureCategoricalAnalysisView
              data={widgetData.data as CategoricalStatResponse}
              widgetConfig={widgetData.config}
              isLoading={isWidgetLoading}
              error={widgetError}
              areaUnit={widgetData.config.displayOptions?.areaUnit || UNIT_AREA_ACRE}
              orgTags={[]}
              sharedView={true}
            />
          ) : (
            <MultiFeatureAnalysisView
              widgetConfig={widgetData.config}
              data={widgetData.data as MFAResponse}
              isLoading={isWidgetLoading}
              error={widgetError}
              noDataFound={!!noDataFound}
              orgTags={[]}
            />
          ))}
      </div>
    </div>
  );
};

export default PublicWidget;
